// country-select.component.ts
import { Component, Inject, DOCUMENT, ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { StateService } from '../../services/state.service';
import { ActivatedRoute } from '@angular/router';

import { environment } from '../../../environments/environment';
import { LANGUAGES } from '../../data/languages';

type LangItem = {
  name: string;
  flag: string;  // iso2 (css sprite class)
  short: string; // base language (e.g. 'nl')
  lang: string;  // full code (e.g. 'nl-NL')
};

@Component({
    selector: 'app-country-select',
    templateUrl: './country-select.component.html',
    styleUrls: ['./country-select.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CountrySelectComponent {
  isOpen = false;
  current!: LangItem;
  items: LangItem[] = [];
  langNames = environment.langNames;

  constructor(
    public translate: TranslateService,
    private state: StateService,
    private route: ActivatedRoute,
    @Inject('Window') private window: Window,
    @Inject(DOCUMENT) public document: Document
  ) {
    const routeLang = this.route.snapshot.paramMap.get('language') || translate.getBrowserLang();
    const initial =
      (this.state.language && this.langNames[this.state.language]?.code) ||
      LANGUAGES[routeLang] ||
      LANGUAGES[translate.getBrowserLang()] ||
      'en-EN';

    this.items = this.translate.getLangs()
      .map((code) => {
        const base = code.split('-')[0];
        const meta = this.langNames[base];
        if (!meta) return null;
        const flag = (meta.flag ? meta.flag : meta.short);
        return {
          name: meta.name,
          flag: (flag == 'en' ? 'gb' : flag),
          short: meta.short,
          lang: code
        } as LangItem;
      })
      .filter((x): x is LangItem => !!x);
// console.log(environment.langNames);
    this.current =
      this.items.find(i => i.lang === initial) ||
      this.items.find(i => i.short === initial.split('-')[0]) ||
      this.items[0];

    if (this.current) {
      // move the current item to the top of the list
      this.items = [
        this.current,
        ...this.items.filter(i => i !== this.current),
      ];
    }

    this.use(this.current);
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
  }

  select(item: LangItem): void {
    this.current = item;
    this.use(item, true);
    this.isOpen = false;
  }

  private use(item: LangItem, force?): void {
    if(force) {
      this.translate.reloadLang(item.short).subscribe(() => {
        this.translate.use(item.short);
      });
    } else {
      this.translate.use(item.short);
    }
    this.state.language = item.short;
  }

  onKeydownList(e: KeyboardEvent, idx: number): void {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = (idx + 1) % this.items.length;
      (this.document.getElementById(`cs-opt-${next}`) as HTMLElement)?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = (idx - 1 + this.items.length) % this.items.length;
      (this.document.getElementById(`cs-opt-${prev}`) as HTMLElement)?.focus();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.select(this.items[idx]);
    } else if (e.key === 'Escape') {
      this.isOpen = false;
    }
  }
}
